import React, { useEffect, useRef, useState } from 'react';

interface AnimatedTitleProps {
  text: string;
  isActive?: boolean;
  baseDelayMs?: number;
  jitterMs?: number;
  weightBoostMs?: number;
}

const heavySet = new Set('bdgkmnpqrtwxyzBDGKMNPQRTWXYZ');
const mediumSet = new Set('acehilosuvACEHILOSUV');

const AnimatedTitle: React.FC<AnimatedTitleProps> = ({
  text,
  isActive = false,
  baseDelayMs = 38,
  jitterMs = 22,
  weightBoostMs = 25,
}) => {
  const [displayed, setDisplayed] = useState('');
  const [showCaret, setShowCaret] = useState(false);
  const timeouts = useRef<NodeJS.Timeout[]>([]);

  const finalText = text || 'New Chat';

  useEffect(() => {
    return () => {
      timeouts.current.forEach(t => clearTimeout(t));
      timeouts.current = [];
    };
  }, []);

  useEffect(() => {
    timeouts.current.forEach(t => clearTimeout(t));
    timeouts.current = [];

    if (!isActive) {
      setDisplayed(finalText);
      setShowCaret(false);
      return;
    }

    setDisplayed('');
    setShowCaret(true);

    const chars = finalText.split('');
    let index = 0;

    const step = () => {
      if (index >= chars.length) {
        setShowCaret(false);
        return;
      }
      const ch = chars[index];
      setDisplayed(prev => prev + ch);
      index++;
      let delay = baseDelayMs + (Math.random() * (jitterMs * 2) - jitterMs);
      if (heavySet.has(ch)) {
        delay += weightBoostMs;
      } else if (mediumSet.has(ch)) {
        delay += Math.round(weightBoostMs * 0.48);
      }
      if (ch === ' ' || ch === ',' || ch === ':' || ch === '—') {
        delay += 30 + Math.random() * 30;
      }
      const timeout = setTimeout(step, delay);
      timeouts.current.push(timeout);
    };

    const first = setTimeout(step, baseDelayMs);
    timeouts.current.push(first);

    return () => {
      timeouts.current.forEach(t => clearTimeout(t));
      timeouts.current = [];
    };
  }, [finalText, isActive, baseDelayMs, jitterMs, weightBoostMs]);

  return (
    <span
      aria-live="polite"
      aria-atomic="true"
      style={{ display: 'inline-block', whiteSpace: 'nowrap' }}
    >
      {displayed}
      {showCaret && <span style={{ opacity: 0.7 }}>▌</span>}
    </span>
  );
};

export default AnimatedTitle;
